import axios from "axios";
import Vue from "vue";

const MAX_USER_AGE_SECONDS = 1800;

let userPromise = null;
let editionPromise = null;

export default {
  state: {
    user: null,
    edition: null,
    redirect: null,
    popups: null,
    userLoaded: null,
  },
  mutations: {
    setEdition(state, edition) {
      state.edition = edition;
      // if the edition uses payment, add the stripe libraries
      if (edition.use_stripe || edition.enable_program_payment) {
        if (!window.Stripe) {
          [
            "https://js.stripe.com/v3/",
            "https://checkout.stripe.com/checkout.js",
          ].forEach((url) => {
            let el = document.createElement("script");
            el.src = url;
            document.body.appendChild(el);
          });
        }
      }
    },
    setUser(state, user) {
      state.user = user;
      state.userLoaded = new Date();
      // identify the user for fullstory
      if (window.FS && user) {
        FS('setIdentity', {
          uid: user.id,
          properties: {
            displayName: user.name,
            email: user.email
          }
        });
      }
    },
    setUserCommunityAcceptedTerms(state, accepted) {
      state.user.community_accepted_terms = accepted;
    },
    setRedirect(state, url) {
      state.redirect = url;
    },
    setUserImage(state, image) {
      if (state.user) {
        state.user.image = image;
      }
    },
    setPopups(state, value) {
      state.popups = value;
    },
    recordCommunityView(state) {
      state.user.last_viewed_community = new Date().toISOString();
    },
    recordJourneysView(state) {
      state.user.seen_journeys = new Date().toISOString();
    },
    setUserHasSubscription(state) {
      // when purchasing a subscription, to make sure we display the billing and payment page
      state.user.has_subscriptions = true;
      state.user.is_in_subscription = true;
      state.user.inactive_subscription = null;
    },
    setUserMfa(state, enabled) {
      Vue.set(state.user, "mfa_enabled", enabled);
    },
  },
  actions: {
    fetchEdition({ commit, state }, args) {
      return new Promise((resolve) => {
        if (state.edition) {
          resolve();
          return;
        }
        if (editionPromise) {
          editionPromise.then(resolve);
          return;
        }
        editionPromise = axios
          .get("/programs/api/edition")
          .then((resp) => {
            commit("setEdition", resp.data);
            resolve();
          })
          .finally(() => {
            editionPromise = null;
          });
      });
    },
    fetchUser({ commit, state, dispatch, getters }, args) {
      return new Promise((resolve, reject) => {
        if (state.user) {
          let shouldReloadUser =
            state.userLoaded === null ||
            (new Date() - state.userLoaded) / 1000 > MAX_USER_AGE_SECONDS;
          if (!shouldReloadUser) {
            resolve();
            return;
          }
        }
        let params = {};
        if (args && args.noRedirect) {
          params.no_redirect = true;
        }
        if (userPromise) {
          userPromise.then(resolve);
          return;
        }
        userPromise = axios
          .get("/programs/api/user", { params })
          .then((resp) => {
            commit("setUser", resp.data.user);
            commit("setRedirect", resp.data.redirect);
            if (!state.popups) {
              dispatch("fetchPopups");
            }
            resolve();
          })
          .catch((err) => {
            commit("setUser", null);
            reject(err);
          })
          .finally(() => {
            userPromise = null;
          });
      });
    },
    fetchUserAndEdition({ dispatch }) {
      return Promise.all([dispatch("fetchUser"), dispatch("fetchEdition")]);
    },
    fetchPopups({ commit, state }) {
      axios.get("/api/activity/popups").then((resp) => {
        commit("setPopups", resp.data);
      });
    },
  },
  getters: {
    currentUser(state) {
      return state.user;
    },
    currentEdition(state) {
      return state.edition;
    },
    forceRedirect(state) {
      return state.redirect;
    },
    userPopups(state) {
      return state.popups;
    },
  },
};
